import React from 'react';
import { Card, Table, Tag, Select, Space, Input, Button, Modal, Typography, message } from 'antd';
import { FileTextOutlined, SearchOutlined, DownloadOutlined, EyeOutlined } from '@ant-design/icons';
import { documentsAPI } from '../services/api';
import { useIsMobile } from '../hooks/useIsMobile';
import { roleLabel } from '../utils/labels';

const { TextArea } = Input;
const { Title, Paragraph } = Typography;


interface DocumentAcceptance {
  id: number;
  userId: number;
  userName: string;
  userEmail: string;
  userRole: string;
  documentKey: string;
  documentTitle: string;
  version: string;
  acceptedAt: string;
  ipAddress: string;
  userAgent: string;
  fingerprint: string;
}

interface DocumentVersion {
  id: number;
  documentKey: string;
  version: string;
  title: string;
  content: string;
  createdAt: string;
  isActive: boolean;
}

const DocumentAcceptances: React.FC = () => {
  const isMobile = useIsMobile();
  const [loading, setLoading] = React.useState(false);
  const [acceptances, setAcceptances] = React.useState<DocumentAcceptance[]>([]);
  const [filteredData, setFilteredData] = React.useState<DocumentAcceptance[]>([]);
  const [filterRole, setFilterRole] = React.useState<string | undefined>(undefined);
  const [filterDoc, setFilterDoc] = React.useState<string | undefined>(undefined);
  const [searchText, setSearchText] = React.useState('');
  
  // Состояние для модального окна документа
  const [documentModalVisible, setDocumentModalVisible] = React.useState(false);
  const [selectedDocument, setSelectedDocument] = React.useState<DocumentVersion | null>(null);
  const [documentVersions, setDocumentVersions] = React.useState<DocumentVersion[]>([]);

  React.useEffect(() => {
    load();
    loadDocumentVersions();
  }, []);

  const loadDocumentVersions = async () => {
    try {
      const docs = await documentsAPI.getDocuments();
      setDocumentVersions(Array.isArray(docs) ? docs : []);
    } catch (error) {
      console.error('Error loading document versions:', error);
      setDocumentVersions([]);
    }
  };

  const openDocument = async (documentKey: string, version: string) => {
    try {
      // Загружаем версии документа из API
      const versions = await documentsAPI.getDocumentVersions(documentKey);
      const document = versions.find(doc => doc.version === version);
      
      if (document) {
        setSelectedDocument(document);
        setDocumentModalVisible(true);
      } else {
        message.error('Документ не найден');
      }
    } catch (error) {
      console.error('Error loading document:', error);
      message.error('Ошибка загрузки документа');
    }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await documentsAPI.getDocumentAcceptances();
      setAcceptances(data);
      setFilteredData(data);
    } catch (e: any) {
      message.error('Ошибка загрузки принятий документов: ' + e.message);
      setAcceptances([]);
      setFilteredData([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    let filtered = acceptances;
    
    if (filterRole) {
      filtered = filtered.filter(a => a.userRole === filterRole);
    }
    
    if (filterDoc) {
      filtered = filtered.filter(a => a.documentKey === filterDoc);
    }
    
    if (searchText) {
      const lower = searchText.toLowerCase();
      filtered = filtered.filter(a =>
        a.userName.toLowerCase().includes(lower) ||
        a.userEmail.toLowerCase().includes(lower) ||
        a.documentTitle.toLowerCase().includes(lower)
      );
    }
    
    setFilteredData(filtered);
  }, [filterRole, filterDoc, searchText, acceptances]);

  const uniqueRoles = Array.from(new Set(acceptances.map(a => a.userRole)));
  const uniqueDocs = Array.from(new Set(acceptances.map(a => a.documentKey)));

  const handleExport = () => {
    let csv = 'Пользователь,Email,Роль,Документ,Версия,Принято,IP,Устройство,Отпечаток\n';
    filteredData.forEach(a => {
      csv += `${a.userName},${a.userEmail},${a.userRole},${a.documentTitle},${a.version},${new Date(a.acceptedAt).toLocaleString('ru')},${a.ipAddress},${a.userAgent},${a.fingerprint}\n`;
    });
    const el = document.createElement('a');
    el.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv));
    el.setAttribute('download', `doc_acceptances_${new Date().toISOString().split('T')[0]}.csv`);
    el.click();
  };

  const columns = [
    {
      title: 'Пользователь',
      key: 'user',
      width: 180,
      ellipsis: true,
      sorter: (a: DocumentAcceptance, b: DocumentAcceptance) => 
        a.userName.localeCompare(b.userName, 'ru'),
      render: (_: any, record: DocumentAcceptance) => (
        <div style={{ minWidth: 0 }}>
          <div style={{ 
            fontWeight: 500, 
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={record.userName}>
            {record.userName}
          </div>
          <div style={{ 
            fontSize: '12px', 
            color: '#666',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} title={record.userEmail}>
            {record.userEmail}
          </div>
        </div>
      )
    },
    {
      title: 'Роль',
      dataIndex: 'userRole',
      key: 'userRole',
      width: 100,
      sorter: (a: DocumentAcceptance, b: DocumentAcceptance) => 
        a.userRole.localeCompare(b.userRole, 'ru'),
      render: (role: string) => {
        const colors: any = { 
          admin: 'red', 
          customer: 'blue', 
          supplier: 'green', 
          employee: 'orange', 
          support: 'purple', 
          delivery: 'cyan' 
        };
        return <Tag color={colors[role]}>{roleLabel(role)}</Tag>;
      }
    },
    {
      title: 'Документ',
      key: 'document',
      width: 200,
      ellipsis: true,
      sorter: (a: DocumentAcceptance, b: DocumentAcceptance) => 
        a.documentTitle.localeCompare(b.documentTitle, 'ru'),
      render: (_: any, record: DocumentAcceptance) => (
        <Button
          type="link"
          style={{ 
            padding: 0, 
            height: 'auto', 
            textAlign: 'left',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            minWidth: 0,
            width: '100%'
          }}
          onClick={() => openDocument(record.documentKey, record.version)}
        >
          <div style={{ 
            fontWeight: 500, 
            color: '#1890ff',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%'
          }} title={record.documentTitle}>
            {record.documentTitle}
          </div>
          <div style={{ 
            fontSize: '11px', 
            color: '#999',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            width: '100%'
          }} title={record.documentKey}>
            {record.documentKey}
          </div>
        </Button>
      )
    },
    {
      title: 'Версия',
      dataIndex: 'version',
      key: 'version',
      width: 70,
      sorter: (a: DocumentAcceptance, b: DocumentAcceptance) => 
        parseFloat(a.version) - parseFloat(b.version),
      render: (version: string) => (
        <span style={{ 
          fontFamily: 'monospace',
          fontSize: '12px',
          fontWeight: 500
        }}>
          v{version}
        </span>
      )
    },
    {
      title: 'Принято',
      dataIndex: 'acceptedAt',
      key: 'acceptedAt',
      width: 160,
      render: (date: string) => (
        <span style={{ fontSize: '12px' }}>
          {new Date(date).toLocaleString('ru')}
        </span>
      ),
      sorter: (a: DocumentAcceptance, b: DocumentAcceptance) => 
        new Date(b.acceptedAt).getTime() - new Date(a.acceptedAt).getTime()
    },
    {
      title: 'IP адрес',
      dataIndex: 'ipAddress',
      key: 'ipAddress',
      width: 120,
      render: (ip: string) => (
        <span style={{ 
          fontFamily: 'monospace', 
          fontSize: '11px',
          color: '#666'
        }}>
          {ip}
        </span>
      )
    },
    {
      title: 'Устройство',
      dataIndex: 'userAgent',
      key: 'userAgent',
      width: 200,
      ellipsis: true,
      render: (ua: string) => (
        <span 
          style={{ 
            fontSize: '11px', 
            color: '#666',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }} 
          title={ua}
        >
          {ua}
        </span>
      )
    },
    {
      title: 'Отпечаток',
      dataIndex: 'fingerprint',
      key: 'fingerprint',
      width: 120,
      ellipsis: true,
      render: (fp: string) => (
        <span 
          style={{ 
            fontFamily: 'monospace', 
            fontSize: '10px', 
            color: '#999',
            display: 'block',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
          title={fp}
        >
          {fp}
        </span>
      )
    }
  ];

  return (
    <>
      <Card
        title={<><FileTextOutlined /> Принятие документов пользователями</>}
        loading={loading}
        extra={
          <Button icon={<DownloadOutlined />} onClick={handleExport}>
            Экспорт CSV
          </Button>
        }
      >
        <Space style={{ marginBottom: '16px' }} wrap>
          <Input
            placeholder="Поиск по имени, email, документу"
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            style={{ width: 300 }}
            allowClear
          />
          <Select
            placeholder="Фильтр по роли"
            allowClear
            value={filterRole}
            onChange={setFilterRole}
            style={{ width: 180 }}
            options={uniqueRoles.map(role => ({ label: role, value: role }))}
          />
          <Select
            placeholder="Фильтр по документу"
            allowClear
            value={filterDoc}
            onChange={setFilterDoc}
            style={{ width: 200 }}
            options={uniqueDocs.map(doc => ({ label: doc, value: doc }))}
          />
        </Space>

        <Table
          rowKey="id"
          columns={columns as any}
          dataSource={filteredData}
          pagination={{ pageSize: 15 }}
          locale={{ emptyText: 'Нет данных о принятии документов' }}
          scroll={isMobile ? { x: 'max-content' } : { x: 1200, y: 600 }}
          size={isMobile ? 'small' : 'middle'}
          bordered
        />
      </Card>

      {/* Модальное окно для просмотра документа */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FileTextOutlined />
            <span>{selectedDocument?.title}</span>
            <Tag color="blue">v{selectedDocument?.version}</Tag>
          </div>
        }
        open={documentModalVisible}
        onCancel={() => setDocumentModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDocumentModalVisible(false)}>
            Закрыть
          </Button>
        ]}
        width={isMobile ? '100%' : 800}
        centered={!isMobile}
        style={isMobile ? { top: 0, paddingBottom: 0 } : { top: 20 }}
      >
        {selectedDocument && (
          <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ 
              background: '#f5f5f5', 
              padding: '12px', 
              borderRadius: '6px', 
              marginBottom: '16px',
              fontSize: '12px',
              color: '#666'
            }}>
              <div><strong>Версия:</strong> {selectedDocument.version}</div>
              <div><strong>Создано:</strong> {new Date(selectedDocument.createdAt).toLocaleString('ru')}</div>
              <div><strong>Статус:</strong> {selectedDocument.isActive ? 'Активная' : 'Архивная'}</div>
            </div>
            
            <div style={{ 
              whiteSpace: 'pre-wrap', 
              fontFamily: 'monospace', 
              fontSize: '13px',
              lineHeight: '1.6',
              background: '#fff',
              padding: '16px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px'
            }}>
              {selectedDocument.content}
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default DocumentAcceptances;

